// src/pages/HomeExcelsior.jsx
import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  IconButton,
  Link,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link as RouterLink } from "react-router-dom";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SchoolIcon from "@mui/icons-material/School";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import MenuBookIcon from "@mui/icons-material/MenuBook";

function ServiceCard({ icon, title, description }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(18, 85, 112, 0.10)",
              color: "primary.main",
            }}
          >
            {icon}
          </Box>

          <Typography variant="h6" fontWeight={900}>
            {title}
          </Typography>

          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {description}
          </Typography>

          <Box>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 0, "&:hover": { backgroundColor: "transparent" } }}
            >
              Saiba mais
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function Section({ id, title, subtitle, children }) {
  return (
    <Box id={id} sx={{ scrollMarginTop: 90 }}>
      <Paper sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Stack spacing={1.2}>
          <Stack spacing={0.5}>
            <Typography variant="h5" fontWeight={900}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Stack>
          <Divider />
          <Box>{children}</Box>
        </Stack>
      </Paper>
    </Box>
  );
}

function InfoRow({ icon, title, text }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          bgcolor: `rgba(18, 85, 112, 0.10)`,
          color: "primary.main",
          flex: "0 0 auto",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography fontWeight={900}>{title}</Typography>
        <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
          {text}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function HomeExcelsior() {
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const [anchorElUser, setAnchorElUser] = React.useState(null);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Top Bar */}
      <AppBar position="sticky" color="default">
        <Toolbar sx={{ borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}>
          <IconButton edge="start" sx={{ mr: 1 }} aria-label="menu">
            <IconButton onClick={handleOpenUserMenu} color="inherit">
              <MenuIcon />
            </IconButton>

            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography
                  component={RouterLink}
                  to="/app/login"
                  sx={{
                    textDecoration: "none",
                    color: "inherit",
                    fontWeight: 600,
                  }}
                >
                  Login
                </Typography>
              </MenuItem>
            </Menu>
          </IconButton>

          {/* Logo placeholder (swap with your PNG/SVG) */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              mr: 1.5,
            }}
          >
            E
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={900} lineHeight={1.1}>
              Excelsior
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Serviços Metrológicos
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <Button onClick={() => scrollToId("servicos")}>Serviços</Button>
            <Button onClick={() => scrollToId("sobre")}>Sobre</Button>
            <Button onClick={() => scrollToId("contato")}>Contato</Button>
          </Stack>

          <Button
            variant="contained"
            color="primary"
            sx={{ ml: 1.5 }}
            onClick={() => scrollToId("contato")}
          >
            Fale conosco
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "background.default",
        }}
      >
        <Container sx={{ py: { xs: 6, md: 9 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2.2}>
                <Box>
                  <Chip
                    label="ABNT NBR ISO/IEC 17025:2017"
                    sx={{
                      bgcolor: "rgba(18, 85, 112, 0.10)",
                      color: "primary.main",
                      fontWeight: 800,
                    }}
                  />
                </Box>

                <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 52 } }}>
                  Bem-vindo à Excelsior
                </Typography>

                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  Oferecemos serviços de metrologia de alta qualidade para
                  atender às suas necessidades.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ pt: 1 }}
                >
                  <Button
                    variant="contained"
                    onClick={() => scrollToId("servicos")}
                  >
                    Ver serviços
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => scrollToId("sobre")}
                  >
                    Conhecer a Excelsior
                  </Button>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Saiba mais sobre nossos serviços e como podemos ajudá-lo a
                  garantir a precisão e a qualidade de seus produtos.
                </Typography>
              </Stack>
            </Grid>

            {/* Right side highlight card */}
            <Grid item xs={12} md={5}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  background:
                    "linear-gradient(180deg, rgba(18, 85, 112, 0.10), rgba(255,255,255, 1))",
                }}
              >
                <Stack spacing={2}>
                  <Typography variant="h5">Excelência em medições</Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    Consultoria, treinamentos e apoio técnico para elevar a
                    confiabilidade metrológica do seu processo.
                  </Typography>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography fontWeight={900}>Contato rápido</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WhatsAppIcon color="primary" />
                      <Typography>(11) 97130-1709</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon color="primary" />
                      <Typography>
                        felipe.reis@excelsiormetrologia.com.br
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About */}
      <Container id="sobre" sx={{ pb: { xs: 6, md: 8 } }}>
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={2}>
              <Typography variant="h5">Sobre a Excelsior</Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.9 }}>
                Fundada em julho de 2022, a Excelsior Serviços Metrológicos foi
                criada com o intuito de disseminar o conhecimento metrológico
                dentro das indústrias, prestadores de serviço de calibração,
                hospitais e engenharias clínicas, com foco nos laboratórios de
                calibração acreditados e postulantes a acreditação da norma ABNT
                NBR ISO/IEC 17025:2017.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>

      {/* AUTOR */}
      <Container id="sobre" sx={{ pb: { xs: 6, md: 8 } }}>
        <Card
          sx={{
            // borderRadius: 4,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "0 12px 32px rgba(2, 6, 23, 0.08)",
          }}
        >
          <Section
            id="autor"
            title="Liderança Técnica em Metrologia e Sistemas de Gestão"
            subtitle="Visão estratégica aplicada à competência laboratorial e acreditação ISO 17025"
          >
            <Stack spacing={3}>
              {/* Executive Summary */}
              <Typography
                color="text.secondary"
                sx={{ lineHeight: 1.9, fontSize: 16 }}
              >
                Profissional com mais de 15 anos de atuação em metrologia,
                especializado na implementação e consolidação de Sistemas de
                Gestão da Qualidade conforme ABNT NBR ISO/IEC 17025:2017. Atua
                no desenvolvimento técnico de laboratórios, estruturação de
                processos, capacitação de equipes e preparação para acreditação
                junto à Cgcre/Inmetro.
              </Typography>

              {/* Strategic Blocks */}
              <Stack spacing={2.5}>
                <InfoRow
                  icon={<PrecisionManufacturingIcon />}
                  title="Experiência Técnica Consolidada"
                  text="Atuação contínua na área desde 2009, com foco em calibração, rastreabilidade metrológica e avaliação da conformidade."
                />

                <InfoRow
                  icon={<VerifiedIcon />}
                  title="Especialista em ISO 17025"
                  text="Qualificado para conduzir auditorias internas, implementar requisitos normativos e estruturar laboratórios para acreditação."
                />

                <InfoRow
                  icon={<SchoolIcon />}
                  title="Formação Acadêmica e Técnica"
                  text="Pós-graduação em Metrologia; Tecnólogo em Mecatrônica Industrial; Técnico em Manutenção de Equipamentos Biomédicos; Instrumentista Industrial."
                />

                <InfoRow
                  icon={<MenuBookIcon />}
                  title="Atuação Institucional e Técnica"
                  text="Participação no subcomitê técnico de calibração (CT-08 – Eletroeletrônica), contribuindo para o desenvolvimento técnico do setor."
                />
              </Stack>

              {/* Executive Highlight */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: "rgba(18, 85, 112, 0.08)",
                  border: "1px solid rgba(18, 85, 112, 0.15)",
                }}
              >
                <Typography fontWeight={900} sx={{ mb: 1 }}>
                  Diferencial Estratégico
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Combina profundidade técnica em metrologia com visão
                  estratégica de gestão, permitindo transformar requisitos
                  normativos em vantagem competitiva para laboratórios e
                  organizações industriais.
                </Typography>
              </Box>
            </Stack>
          </Section>
        </Card>
      </Container>

      {/* Services */}
      <Container id="servicos" sx={{ pb: { xs: 7, md: 9 } }}>
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={900}>
            Nossos Serviços
          </Typography>
          <Typography color="text.secondary">
            Soluções práticas para precisão, conformidade e melhoria contínua.
          </Typography>
        </Stack>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <ServiceCard
              icon={<PrecisionManufacturingIcon />}
              title="Metrologia"
              description="Realizamos consultoria em metrologia para garantir a precisão dos resultados. ABNT NBR ISO/IEC 17025:2017."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ServiceCard
              icon={<SupportAgentIcon />}
              title="Consultoria"
              description="Oferecemos consultoria em metrologia para ajudar sua empresa a implementar melhores práticas de medição."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ServiceCard
              icon={<SchoolIcon />}
              title="Treinamentos"
              description="Oferecemos treinamento em metrologia para ajudar sua empresa a implementar melhores práticas de medição."
            />
          </Grid>
        </Grid>
      </Container>

      {/* Contact / Footer band */}
      <Box
        id="contato"
        sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
      >
        <Container sx={{ py: { xs: 5, md: 6 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h4" fontWeight={900} sx={{ mb: 1 }}>
                Pronto para melhorar a qualidade das suas medições?
              </Typography>
              <Typography sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                Entre em contato conosco e descreva seu cenário. Nós ajudamos a
                definir o melhor caminho (diagnóstico, consultoria e/ou
                treinamento).
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  bgcolor: "rgba(255,255,255,0.10)",
                  borderColor: "rgba(255,255,255,0.20)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={1.5}>
                    <Typography fontWeight={900}>
                      Entre em contato conosco:
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <WhatsAppIcon />
                      <Typography>(11) 97130-1709</Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon />
                      <Typography>
                        felipe.reis@excelsiormetrologia.com.br
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="inherit"
                        sx={{
                          bgcolor: "#FFFFFF",
                          color: "primary.main",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
                        }}
                        startIcon={<WhatsAppIcon />}
                        onClick={() =>
                          window.open("https://wa.me/5511971301709", "_blank")
                        }
                      >
                        WhatsApp
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        color="inherit"
                        sx={{
                          borderColor: "rgba(255,255,255,0.55)",
                          color: "#FFFFFF",
                          "&:hover": { borderColor: "#FFFFFF" },
                        }}
                        startIcon={<EmailIcon />}
                        onClick={() =>
                          (window.location.href =
                            "mailto:felipe.reis@excelsiormetrologia.com.br")
                        }
                      >
                        E-mail
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.18)" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
          >
            <Typography sx={{ opacity: 0.85 }}>
              © {new Date().getFullYear()}. All rights reserved.
            </Typography>
            <Typography sx={{ opacity: 0.85 }}>
              <Link
                href="#"
                underline="hover"
                color="inherit"
                sx={{ opacity: 0.95, fontWeight: 700 }}
                onClick={(e) => e.preventDefault()}
              >
                Excelsior
              </Link>
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
